import { useState, useEffect } from "preact/compat";
import { useLazyQuery } from "@apollo/client";
import MENU_QUERY from "@/queries/menuQuery";
import useLocalStorageState from "use-local-storage-state";
import { useSearchParams } from "react-router-dom";
import { sortValues } from "@/Utils";

function useMenu(categorySlug, subCategorySlug, brandID, specialID, special, specialItems) {
	const perPage = 24; // Set the Products per page
	const [status, setStatus] = useState('loading');
	const [products, setProducts] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [productCount, setProductCount] = useState(0);
	const [pages, setPages] = useState();
	const [retailerObject, setRetailerObject] = useLocalStorageState("dutchie_retailer");
	const retailerId = retailerObject?.id;

	let [searchParams, setSearchParams] = useSearchParams();
	const [search, setSearch] = useState("");
	const [filters, setFilters] = useState({});
	const [sort, setSort] = useState({ key: "POPULAR", direction: "DESC" });

	let defaultSort = searchParams.get("sort");
	let categoryEnum, subcategoryEnum;
	
	if ( categorySlug ) {
		categoryEnum = categorySlug?.toUpperCase().replace(/[-\s]+/g, "_");
	}

	if ( subCategorySlug ) {
		subcategoryEnum = subCategorySlug?.toUpperCase().replace(/[-\s]+/g, "_");
	}

	if ( defaultSort ) {
		defaultSort = sortValues[defaultSort];
	} else {
		defaultSort = sortValues["POPULAR"];
	}

	let [currentSort, setCurrentSort] = useState(defaultSort);

	useEffect(() => {
		const defaultSort = searchParams.get("sort") || "POPULAR";
		const sortValue = sortValues[defaultSort];
		setSort({ key: sortValue.value, direction: sortValue.direction });

		setFilters({
			...(searchParams.get("category") && {
				category: searchParams.get("category"),
			}),
			...(searchParams.get("strainType") && {
				strainType: searchParams.get("strainType"),
			}),
			...(searchParams.get("subcategory") && {
				subcategory: searchParams.get("subcategory"),
			}),
			...(searchParams.get("weight") && {
				weights: searchParams.get("weight"),
			}),
			...(searchParams.getAll("tags[]").length > 0 && {
				tags: searchParams.getAll("tags[]"),
			}),
			...(searchParams.getAll("effects[]").length > 0 && {
				effects: searchParams.getAll("effects[]"),
			}),
			...(searchParams.getAll("brand[]").length > 0 && { 
				brandIds: searchParams.getAll("brand[]")
			}),
			...(searchParams.get("potencyTHC") && {
				potencyThc: {
					min: parseInt( searchParams.get("potencyTHC").split(",")[0], 10 ),
					max: parseInt( searchParams.get("potencyTHC").split(",")[1], 10 ),
					unit: searchParams.get("unit"),
				},
			}),
			...(searchParams.get("search") && { search: searchParams.get("search") }),
		});

		setCurrentPage(searchParams.get("pagenum") || 1);
	}, [searchParams]);

	function handleSortChange(sortValue, sortName) {
		searchParams.set("sort", sortValue);
		searchParams.set("pagenum", 1);
		setSearchParams(searchParams);
		setCurrentSort(sortName);
		const key = sortValues[sortValue].value;
		const direction = sortValues[sortValue].direction;
		setSort({ key: key, direction: direction });
		setCurrentPage(1);
	}

	function clearFilters() {
		searchParams.delete("subcategory");
		searchParams.delete("weight");
		searchParams.delete("brand[]");
		searchParams.delete("strainType");
		searchParams.delete("category");
		searchParams.delete("tags[]");
		searchParams.delete("effects[]");
		searchParams.delete("potencyTHC");
		searchParams.delete("unit");
		searchParams.delete("sort");
		// setFilters({});
		// setCurrentPage(1);
		setSearchParams(searchParams);
	}

	function handleFilterChange(FormEvent) {
		const form = document.querySelector('.shop-filters-form');
		let formData = new FormData(form);
		let brand = formData.getAll("brand[]");
		let strainType = formData.get("strainType");
		let subcategory = formData.get("subcategory");
		let category = formData.get("category");
		let weight = formData.get("weight");
		let tags = formData.getAll("tags[]");
		let effects = formData.getAll("effects[]");
		let potencyTHC = formData.getAll("THC[]");
        potencyTHC = potencyTHC.map(Number); // Convert Potency THC to Number.
		let selectedUnit = formData.get("unit");

		setCurrentPage(1);
		const newSearchParams = new URLSearchParams();

		if (brand.length > 0) {
			brand.forEach((brand) => newSearchParams.append("brand[]", brand));
		}

		if (strainType) {
			newSearchParams.set("strainType", strainType);
		}

		if (category) {
			newSearchParams.set("category", category);
		}

		if (subcategory) {
			newSearchParams.set("subcategory", subcategory);
		}

		if (weight) {
			newSearchParams.set("weight", weight);
		}

		if (effects.length > 0) {
			effects.forEach((effect) => newSearchParams.append("effects[]", effect));
		}

		if (tags.length > 0) {
			tags.forEach((tag) => newSearchParams.append("tags[]", tag));
		}

		if ( potencyTHC ) {
			newSearchParams.set("potencyTHC", potencyTHC);
			newSearchParams.set("unit", selectedUnit);
		}

		newSearchParams.set(
			"sort",
			searchParams.get("sort") ? searchParams.get("sort") : "POPULAR"
		);

		setSearchParams(newSearchParams);
	}

	const variables = {
		retailerId: retailerId,
		offset: (currentPage - 1) * perPage,
		limit: perPage,
		filter: {
			...filters,
			...(categoryEnum && { category: categoryEnum }), // Conditionally include categorySlug in the filter
			...(subcategoryEnum && { subcategory: subcategoryEnum }), // Conditionally include subcategorySlug in the filter
			...(brandID && { brandId: brandID }), // Conditionally include brandId in the filter
			...(specialID && { menuSection: { type: 'SPECIALS', specialId: specialID }}),
			...(specialItems && { menuSection: { type: 'SPECIALS' }})
		},
		sort: sort,
	};

	const [fetchProducts, { loading, error, data }] = useLazyQuery(MENU_QUERY, {
		variables: variables,
		onCompleted: (data) => {
			setStatus('done');
		},
	});

	useEffect(() => {
		if ( !retailerId || !sort ) {
			return;
		}
		fetchProducts();
	}, [searchParams, retailerId]);

	useEffect(() => {
		if (data) {

			if ( special && data.menu.products ) {
				const productArray = data.menu?.products.map(product => ({
					...product,
					coupon: special.id,
					promotion_name: special.name,
					promotion_id: special.id,
				}));
				setProducts(productArray);
			} else {
				setProducts(data.menu?.products);
			}

			setProductCount(data.menu?.productsCount);
			setPages(Math.ceil(data.menu?.productsCount / perPage));
		}
	}, [data]);
	
	return {
		products,
		setProducts,
		status,
		loading,
		productCount,
		setProductCount,
		pages,
		currentPage,
		setCurrentPage,
		handleFilterChange,
		currentSort,
		setCurrentSort,
		sortValues,
		handleSortChange,
		searchParams,
		setSearchParams,
		clearFilters,
	};
}
export default useMenu;